import * as React from 'react';

import { useDndContext, type UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cva } from 'class-variance-authority';

import { cn } from '../../../../lib/stylingUtils';

import { Task, TaskCard } from './KanbanTask';

const kanbanBoardContainerVariants = cva(
  'flex size-full flex-col justify-start gap-2 p-2',
  {
    variants: {
      dragging: {
        default: 'snap-x snap-mandatory',
        active: 'cursor-grabbing snap-none',
      },
    },
  },
);

const kanbanColumnVariants = cva(
  [
    'h-24 max-h-36 min-w-full',
    'flex snap-center gap-2 overflow-auto',
    'rounded-lg border border-foreground/10 bg-foreground/5',
  ],
  {
    variants: {
      dragging: {
        default: 'border-2 border-transparent',
        over: 'opacity-30 ring-2',
        overlay: 'ring-2 ring-primary',
      },
    },
  },
);

const kanbanColumnHeaderVariants = cva(['w-48 min-w-48 bg-space p-2 pb-0'], {
  variants: {
    dragging: {
      over: 'cursor-grabbing',
      overlay: 'cursor-grabbing',
    },
    pinned: {
      true: 'cursor-auto',
      false: 'cursor-grab',
    },
  },
});

const boardColumnContentStyle = cva(['flex w-full overflow-x-auto p-0']);

export interface Column {
  id: UniqueIdentifier;
  title: string;
  header?: () => React.ReactNode;
}

export type ColumnType = 'Column';

export interface ColumnDragData {
  type: ColumnType;
  column: Column;
}

export interface BoardColumnProps {
  column: Column;
  tasks: Task[];
  isOverlay?: boolean;
  onTaskClick?: (taskId: UniqueIdentifier) => void;
}

export const KanbanColumn = ({
  column,
  tasks,
  onTaskClick,
  isOverlay,
}: BoardColumnProps) => {
  const tasksIds = React.useMemo(() => tasks.map((task) => task.id), [tasks]);

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    } satisfies ColumnDragData,
    attributes: {
      roleDescription: `Column: ${column.title}`,
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@KanbanColumn',
        kanbanColumnVariants({
          dragging: isOverlay ? 'overlay' : isDragging ? 'over' : undefined,
        }),
      )}
    >
      <div
        className={kanbanColumnHeaderVariants({
          dragging: isOverlay ? 'overlay' : isDragging ? 'over' : undefined,
          pinned: false,
        })}
        {...attributes}
        {...listeners}
      >
        {column.header?.() ?? column.title}
      </div>
      <div className={boardColumnContentStyle()}>
        <div className="flex gap-2 p-2">
          <SortableContext items={tasksIds}>
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={!isOverlay ? () => onTaskClick?.(task.id) : undefined}
              />
            ))}
          </SortableContext>
        </div>
      </div>
    </div>
  );
};

export const PinnedKanbanColumn = ({
  column,
  tasks,
  onTaskClick,
}: BoardColumnProps) => {
  const tasksIds = React.useMemo(() => tasks.map((task) => task.id), [tasks]);

  const { setNodeRef, attributes } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    } satisfies ColumnDragData,
    attributes: {
      roleDescription: `Column: ${column.title}`,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn('@PinnedKanbanColumn', kanbanColumnVariants())}
      {...attributes}
    >
      <div className={kanbanColumnHeaderVariants({ pinned: true })}>
        {column.header?.() ?? column.title}
      </div>
      <div className={boardColumnContentStyle()}>
        <div className="flex gap-2 p-2">
          <SortableContext items={tasksIds}>
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={() => onTaskClick?.(task.id)}
              />
            ))}
          </SortableContext>
        </div>
      </div>
    </div>
  );
};

export type KanbanBoardContainerProps = {
  children: React.ReactNode;
};

export const KanbanBoardContainer = ({
  children,
}: KanbanBoardContainerProps) => {
  const dndContext = useDndContext();

  return (
    <div
      className={cn(
        '@KanbanBoardContainer',
        kanbanBoardContainerVariants({
          dragging: dndContext.active ? 'active' : 'default',
        }),
      )}
    >
      {children}
    </div>
  );
};
